import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "wouter";
import { useGetSession, useGenerateStudyOutputs, useGetChatMessages, type Mcq } from "@workspace/api-client-react";
import { Card, Button, Badge, Textarea } from "@/components/ui";
import { Loader2, Wand2, Send, CheckCircle2, XCircle, AlertCircle, Brain, HelpCircle, BookOpen, Cat, List, Grid3x3, Plus, X, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useChatStream } from "@/hooks/use-chat-stream";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import lerniCatFace from "@assets/lerni-cat-head.png";

export default function SessionDetail() {
  const params = useParams();
  const id = parseInt(params.id || "0", 10);
  const { data: session, isLoading, refetch } = useGetSession(id);
  const [activeTab, setActiveTab] = useState<"overview" | "flashcards" | "mcqs" | "chat">("overview");

  const { mutate: generate, isPending: isGenerating } = useGenerateStudyOutputs({
    mutation: { onSuccess: () => refetch() }
  });

  if (isLoading) {
    return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!session) return <div>Session not found</div>;

  if (!session.hasOutputs) {
    return (
      <div className="max-w-2xl mx-auto mt-20 text-center animate-in fade-in duration-500">
        <img 
          src={`${import.meta.env.BASE_URL}images/empty-state.png`} 
          alt="Ready to generate" 
          className="w-64 h-auto mx-auto mb-8 drop-shadow-xl rounded-2xl"
        />
        <h1 className="text-3xl font-bold font-display">{session.title}</h1>
        <p className="mt-4 text-muted-foreground text-lg">Your material is ready. Let our AI analyze it and generate your comprehensive study package.</p>
        <Button 
          size="lg" 
          className="mt-8 h-14 px-8 text-lg rounded-full" 
          onClick={() => generate({ id, data: {} })}
          isLoading={isGenerating}
        >
          <Wand2 className="w-5 h-5 mr-2" />
          Generate Study Material
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto animate-in fade-in duration-300">
      <div className="mb-8">
        <Badge variant="outline" className="mb-2 bg-card uppercase">{session.sourceType}</Badge>
        <h1 className="text-3xl font-bold font-display">{session.title}</h1>
      </div>

      <div className="flex space-x-1 border-b border-border mb-6 overflow-x-auto no-scrollbar">
        {[
          { key: "overview", label: "Overview" },
          { key: "flashcards", label: "Flashcards" },
          { key: "mcqs", label: "MCQs" },
          { key: "chat", label: "Lerni Bot" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={cn(
              "px-6 py-3 font-semibold text-sm whitespace-nowrap border-b-2 transition-colors",
              activeTab === tab.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-[500px]">
        <AnimatePresence mode="wait">
          {activeTab !== "chat" && (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="h-full"
            >
              {activeTab === "overview" && <OverviewTab summary={session.summary} />}
              {activeTab === "flashcards" && <FlashcardsTab cards={session.flashcards} sessionId={id} onCardAdded={refetch} />}
              {activeTab === "mcqs" && (
                <McqsTab
                  mcqs={session.mcqs}
                  sessionId={id}
                  sessionTitle={session.title}
                  onRegenerateSuccess={refetch}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <div className={activeTab === "chat" ? "h-full" : "hidden"}>
          <ChatTab sessionId={id} sessionTitle={session.title} fileName={session.uploadedFileName} />
        </div>
      </div>
    </div>
  );
}

function OverviewTab({ summary }: { summary: any }) {
  if (!summary) return <div>No summary available</div>;

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-6">
        <Card className="p-6 sm:p-8">
          <h2 className="text-xl font-bold mb-4 font-display">Detailed Summary</h2>
          <div className="prose prose-sm sm:prose-base max-w-none text-foreground/90 dark:prose-invert">
            <ReactMarkdown>{summary.detailedSummary || ""}</ReactMarkdown>
          </div>
        </Card>
      </div>
      
      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="font-bold mb-4 font-display text-lg">Key Points</h3>
          <ul className="space-y-3">
            {summary.keyPoints?.map((pt: string, i: number) => (
              <li key={i} className="flex gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="leading-snug">{pt}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function FlashcardsTab({ cards: initialCards, sessionId, onCardAdded }: { cards: any[]; sessionId: number; onCardAdded?: () => void }) {
  const [cards, setCards] = useState(initialCards || []);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [showAddModal, setShowAddModal] = useState(false);
  const [listRevealedIds, setListRevealedIds] = useState<Set<number>>(new Set());
  const [showAddToast, setShowAddToast] = useState(false);

  if (cards.length === 0 && !showAddModal) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <p className="text-muted-foreground mb-4">No flashcards generated.</p>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Card
        </Button>
      </div>
    );
  }

  const card = cards[index];
  const next = () => { setFlipped(false); setTimeout(() => setIndex((i) => Math.min(i + 1, cards.length - 1)), 150); };
  const prev = () => { setFlipped(false); setTimeout(() => setIndex((i) => Math.max(i - 1, 0)), 150); };

  const handleAddCard = (newCard: { question: string; answer: string }) => {
    const token = localStorage.getItem("studyflow_token");
    fetch(`${API_BASE}/api/sessions/${sessionId}/flashcards`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(newCard),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.id) {
          setCards((prev) => [...prev, data]);
        } else {
          setCards((prev) => [...prev, { id: Date.now(), ...newCard }]);
        }
        setShowAddToast(true);
        setTimeout(() => setShowAddToast(false), 2500);
        onCardAdded?.();
      })
      .catch(() => {
        setCards((prev) => [...prev, { id: Date.now(), ...newCard }]);
        setShowAddToast(true);
        setTimeout(() => setShowAddToast(false), 2500);
      });
    setShowAddModal(false);
  };

  const toggleListReveal = (id: number) => {
    setListRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-2xl mx-auto flex flex-col items-center">
      <div className="mb-6 flex items-center justify-between w-full">
        <Badge variant="outline">
          {viewMode === "card" ? `Card ${index + 1} of ${cards.length}` : `${cards.length} cards`}
        </Badge>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode(viewMode === "card" ? "list" : "card")}
            className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
            title={viewMode === "card" ? "List view" : "Card view"}
          >
            {viewMode === "card" ? <List className="w-4 h-4" /> : <Grid3x3 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="p-2 rounded-lg border border-border hover:bg-muted transition-colors"
            title="Add card"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === "card" ? (
        <>
          <div
            className="w-full h-80 sm:h-96 cursor-pointer preserve-3d relative perspective-[1000px]"
            onClick={() => setFlipped(!flipped)}
          >
            <motion.div
              className="w-full h-full preserve-3d"
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
            >
              <Card className="absolute inset-0 backface-hidden flex flex-col items-center justify-center p-8 text-center bg-card border-2 hover:border-primary/50 transition-colors shadow-lg">
                <span className="text-sm font-bold text-primary mb-4 uppercase tracking-widest">Question</span>
                <h3 className="text-2xl font-display font-medium text-foreground">{card?.question}</h3>
                <p className="mt-6 text-xs text-muted-foreground">Click to reveal answer</p>
              </Card>
              <Card className="absolute inset-0 backface-hidden rotate-y-180 flex flex-col items-center justify-center p-8 text-center bg-primary text-primary-foreground border-none shadow-xl">
                <span className="text-sm font-bold text-primary-foreground/70 mb-4 uppercase tracking-widest">Answer</span>
                <p className="text-xl font-medium leading-relaxed">{card?.answer}</p>
              </Card>
            </motion.div>
          </div>
          <div className="flex gap-4 mt-8">
            <Button variant="outline" onClick={prev} disabled={index === 0}>Previous</Button>
            <Button onClick={next} disabled={index === cards.length - 1}>Next Card</Button>
          </div>
        </>
      ) : (
        <div className="w-full space-y-3">
          {cards.map((c: any, i: number) => {
            const revealed = listRevealedIds.has(c.id);
            return (
              <Card key={c.id || i} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-primary">Q{i + 1}</span>
                      <span className="text-sm font-medium">{c.question}</span>
                    </div>
                    {revealed && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="mt-2 pl-6 text-sm text-muted-foreground border-l-2 border-primary/20"
                      >
                        {c.answer}
                      </motion.div>
                    )}
                  </div>
                  <button
                    onClick={() => toggleListReveal(c.id)}
                    className="p-1.5 rounded hover:bg-muted transition-colors shrink-0"
                    title={revealed ? "Hide answer" : "Show answer"}
                  >
                    {revealed ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-muted-foreground" />}
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showAddModal && (
        <AddFlashcardModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddCard}
        />
      )}

      <AnimatePresence>
        {showAddToast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 text-white text-sm font-semibold shadow-xl shadow-emerald-500/25"
          >
            <CheckCircle2 className="w-4 h-4" />
            Flashcard added successfully
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AddFlashcardModal({ onClose, onAdd }: { onClose: () => void; onAdd: (card: { question: string; answer: string }) => void }) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !answer.trim()) return;
    onAdd({ question: question.trim(), answer: answer.trim() });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded hover:bg-muted">
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-bold font-display mb-4">Add Flashcard</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Question *</label>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter the question..."
              className="min-h-[80px] resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Answer *</label>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter the answer..."
              className="min-h-[80px] resize-none"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!question.trim() || !answer.trim()}>Add Card</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

const MAX_GENERATIONS = 3;

function McqsTab({
  mcqs: initialMcqs,
  sessionId,
  sessionTitle,
  onRegenerateSuccess,
}: {
  mcqs: Mcq[];
  sessionId?: number;
  sessionTitle?: string;
  onRegenerateSuccess?: () => void;
}) {
  // Local MCQ state — starts from props, extended by regenerations
  const [localMcqs, setLocalMcqs] = useState<Mcq[]>(initialMcqs);

  // Sync if parent provides more data (e.g., after refetch completes)
  useEffect(() => {
    if (initialMcqs.length > localMcqs.length) {
      setLocalMcqs(initialMcqs);
    }
  }, [initialMcqs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive generations map: generationNumber → questions
  const generationsMap = useMemo(() => {
    const map = new Map<number, Mcq[]>();
    for (const m of localMcqs) {
      const gen = m.generationNumber ?? 1;
      if (!map.has(gen)) map.set(gen, []);
      map.get(gen)!.push(m);
    }
    return map;
  }, [localMcqs]);

  const existingGenerations = useMemo(
    () => [...generationsMap.keys()].sort((a, b) => a - b),
    [generationsMap]
  );
  const maxGen = existingGenerations[existingGenerations.length - 1] ?? 1;
  const remainingRegenerations = MAX_GENERATIONS - maxGen;

  // Active generation — default to latest
  const [activeGeneration, setActiveGeneration] = useState(maxGen);

  // Per-generation answers: { [genNumber]: { [mcqId]: 'A'|'B'|'C'|'D' } }
  const [generationAnswers, setGenerationAnswers] = useState<Record<number, Record<number, string>>>(() => {
    const result: Record<number, Record<number, string>> = {};
    for (let g = 1; g <= MAX_GENERATIONS; g++) {
      try {
        const saved = localStorage.getItem(`lerni_mcq_answers_${sessionId}_gen${g}`);
        result[g] = saved ? JSON.parse(saved) : {};
      } catch {
        result[g] = {};
      }
    }
    // Migrate legacy key (gen1 without suffix)
    if (Object.keys(result[1] ?? {}).length === 0) {
      try {
        const legacy = localStorage.getItem(`lerni_mcq_answers_${sessionId}`);
        if (legacy) {
          result[1] = JSON.parse(legacy);
          localStorage.removeItem(`lerni_mcq_answers_${sessionId}`);
        }
      } catch {}
    }
    return result;
  });

  // Per-generation results visibility
  const [generationResults, setGenerationResults] = useState<Record<number, boolean>>(() => {
    const result: Record<number, boolean> = {};
    for (let g = 1; g <= MAX_GENERATIONS; g++) {
      try {
        result[g] = localStorage.getItem(`lerni_mcq_results_${sessionId}_gen${g}`) === "true";
      } catch {
        result[g] = false;
      }
    }
    // Migrate legacy key
    if (!result[1]) {
      try {
        result[1] = localStorage.getItem(`lerni_mcq_results_${sessionId}`) === "true";
        if (result[1]) localStorage.removeItem(`lerni_mcq_results_${sessionId}`);
      } catch {}
    }
    return result;
  });

  // Session-level star (once earned, never reset)
  const [starEarned, setStarEarned] = useState(() => {
    try { return localStorage.getItem(`lerni_mcq_star_${sessionId}`) === "true"; } catch { return false; }
  });

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  // Persist state changes
  const answers = generationAnswers[activeGeneration] ?? {};
  const showResults = generationResults[activeGeneration] ?? false;
  const currentMcqs = generationsMap.get(activeGeneration) ?? [];

  useEffect(() => {
    try { localStorage.setItem(`lerni_mcq_answers_${sessionId}_gen${activeGeneration}`, JSON.stringify(answers)); } catch {}
  }, [answers, activeGeneration, sessionId]);

  useEffect(() => {
    try { localStorage.setItem(`lerni_mcq_results_${sessionId}_gen${activeGeneration}`, String(showResults)); } catch {}
  }, [showResults, activeGeneration, sessionId]);

  useEffect(() => {
    try { localStorage.setItem(`lerni_mcq_star_${sessionId}`, String(starEarned)); } catch {}
  }, [starEarned, sessionId]);

  const setAnswers = (updater: Record<number, string> | ((prev: Record<number, string>) => Record<number, string>)) => {
    setGenerationAnswers((prev) => ({
      ...prev,
      [activeGeneration]: typeof updater === "function" ? updater(prev[activeGeneration] ?? {}) : updater,
    }));
  };

  const setShowResults = (val: boolean) => {
    setGenerationResults((prev) => ({ ...prev, [activeGeneration]: val }));
  };

  const score = Object.entries(answers).filter(([id, ans]) => {
    const q = currentMcqs.find((m) => String(m.id) === id);
    return q && q.correctAnswer?.trim().charAt(0).toUpperCase() === ans;
  }).length;

  const handleCheckAnswers = () => {
    setShowResults(true);
    if (starEarned) return; // star already earned for this session
    const pct = currentMcqs.length > 0 ? Math.round((score / currentMcqs.length) * 100) : 0;
    if (pct >= 80 && sessionId) {
      const token = localStorage.getItem("studyflow_token");
      fetch(`${API_BASE}/api/space/stars`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId, sessionTitle: sessionTitle || "Untitled", score: pct }),
      })
        .then((r) => r.json())
        .then((data) => { if (data.star) setStarEarned(true); })
        .catch(() => {});
    }
  };

  const handleTryAgain = () => {
    setAnswers({});
    setShowResults(false);
    // starEarned is intentionally NOT reset — it's session-level
  };

  const handleRegenerate = async () => {
    if (isRegenerating || remainingRegenerations <= 0 || !sessionId) return;
    setIsRegenerating(true);
    setRegenerateError(null);
    try {
      const token = localStorage.getItem("studyflow_token");
      const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/regenerate-mcqs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setRegenerateError(data.message || "Failed to regenerate. Please try again.");
        return;
      }
      if (data.mcqs && data.generationNumber) {
        setLocalMcqs((prev) => [...prev, ...data.mcqs]);
        setActiveGeneration(data.generationNumber);
        onRegenerateSuccess?.(); // update React Query cache in background
      }
    } catch {
      setRegenerateError("Network error. Please try again.");
    } finally {
      setIsRegenerating(false);
    }
  };

  if (!localMcqs || localMcqs.length === 0) return <div>No quizzes available.</div>;

  return (
    <div className="max-w-3xl mx-auto pb-20">
      {/* Generation controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        {/* Generation switcher */}
        <div className="flex items-center gap-1">
          {existingGenerations.map((gen) => (
            <button
              key={gen}
              onClick={() => setActiveGeneration(gen)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all",
                activeGeneration === gen
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground"
              )}
            >
              Set {gen}
            </button>
          ))}
        </div>

        {/* Regenerate controls */}
        <div className="flex items-center gap-3">
          {remainingRegenerations > 0 && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {remainingRegenerations} regeneration{remainingRegenerations !== 1 ? "s" : ""} left
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={isRegenerating || remainingRegenerations <= 0}
            isLoading={isRegenerating}
            className="shrink-0"
          >
            {!isRegenerating && <RefreshCw className="w-4 h-4 mr-2" />}
            Regenerate
          </Button>
        </div>
      </div>

      {/* Max regenerations notice */}
      {remainingRegenerations === 0 && (
        <p className="text-xs text-muted-foreground text-center mb-5">
          You've reached the maximum number of regenerations for this session.
        </p>
      )}

      {/* Regenerate error */}
      {regenerateError && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-xl px-4 py-3 mb-5">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{regenerateError}</span>
        </div>
      )}

      {/* Score card */}
      <AnimatePresence>
        {showResults && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="p-6 bg-primary/10 border-primary/20 flex flex-col items-center text-center mb-8">
              <h3 className="text-2xl font-display font-bold text-primary">
                Score: {score} / {currentMcqs.length}
              </h3>
              <p className="text-muted-foreground mt-1">
                {score === currentMcqs.length
                  ? "Perfect! You've mastered this set."
                  : Math.round((score / currentMcqs.length) * 100) >= 80
                  ? "Great work! You've passed this set."
                  : "Keep studying, you'll get there!"}
              </p>
              {starEarned && (
                <p className="text-sm text-purple-500 mt-2 font-medium">
                  ✨ Star unlocked! Check your Space to see it.
                </p>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Questions for active generation */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeGeneration}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.15 }}
          className="space-y-6"
        >
          {currentMcqs.map((q, i) => {
            const isCorrect = answers[q.id] === q.correctAnswer;

            return (
              <Card key={q.id} className="p-6 sm:p-8">
                <div className="flex gap-4 mb-6">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold text-sm shrink-0">
                    {i + 1}
                  </div>
                  <h3 className="text-lg font-medium pt-1">{q.question}</h3>
                </div>

                <div className="space-y-3 pl-12">
                  {(["A", "B", "C", "D"] as const).map((opt) => {
                    const choiceText = q[`choice${opt}` as keyof Mcq] as string;
                    const isSelected = answers[q.id] === opt;

                    let btnClass = "border-border hover:bg-muted text-foreground";
                    if (showResults) {
                      if (q.correctAnswer === opt) btnClass = "bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400 font-medium";
                      else if (isSelected) btnClass = "bg-destructive/10 border-destructive text-destructive";
                      else btnClass = "opacity-50 border-border bg-transparent";
                    } else if (isSelected) {
                      btnClass = "border-primary bg-primary/5 text-primary font-medium";
                    }

                    return (
                      <button
                        key={opt}
                        disabled={showResults}
                        onClick={() => setAnswers((p) => ({ ...p, [q.id]: opt }))}
                        className={cn(
                          "w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between",
                          btnClass
                        )}
                      >
                        <span>
                          <span className="font-bold mr-3 opacity-50">{opt}.</span>
                          {choiceText}
                        </span>
                        {showResults && q.correctAnswer === opt && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                        {showResults && isSelected && q.correctAnswer !== opt && <XCircle className="w-5 h-5 text-destructive" />}
                      </button>
                    );
                  })}
                </div>

                {showResults && (
                  <div className="mt-6 pl-12">
                    <div
                      className={cn(
                        "p-4 rounded-xl text-sm",
                        isCorrect
                          ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      <strong className="block mb-1">Explanation:</strong>
                      {q.explanation}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </motion.div>
      </AnimatePresence>

      {/* Sticky action button */}
      <div className="sticky bottom-6 flex justify-center mt-8">
        {showResults ? (
          <Button
            size="lg"
            className="shadow-xl px-12 rounded-full h-14 text-lg"
            onClick={handleTryAgain}
          >
            Try Again
          </Button>
        ) : (
          <Button
            size="lg"
            className="shadow-xl px-12 rounded-full h-14 text-lg"
            onClick={handleCheckAnswers}
            disabled={Object.keys(answers).length < currentMcqs.length}
          >
            Check Answers
          </Button>
        )}
      </div>
    </div>
  );
}

const ACTION_CARDS = [
  { label: "Explain it simply", icon: Brain, gradient: "from-purple-500/20 to-blue-500/10", border: "border-purple-500/20 hover:border-purple-400/40", special: null as string | null },
  { label: "Test me", icon: HelpCircle, gradient: "from-blue-500/20 to-cyan-500/10", border: "border-blue-500/20 hover:border-blue-400/40", special: "open_question" },
  { label: "What should I focus on?", icon: BookOpen, gradient: "from-emerald-500/20 to-teal-500/10", border: "border-emerald-500/20 hover:border-emerald-400/40", special: null },
  { label: "Say meow 🐱", icon: Cat, gradient: "from-amber-500/20 to-orange-500/10", border: "border-amber-500/20 hover:border-amber-400/40", special: "meow" },
];

const MEOW_RESPONSES = ["meow 🐾", "meow 😼", "meow 🐱✨"];

function ChatTab({ sessionId, sessionTitle, fileName }: { sessionId: number; sessionTitle?: string; fileName?: string | null }) {
  const { data: chatHistory, isLoading: isLoadingHistory } = useGetChatMessages(sessionId);
  const { messages, sendMessage, isStreaming, error, setMessages } = useChatStream(sessionId);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevSessionRef = useRef(sessionId);

  useEffect(() => {
    setMessages([]);
  }, [sessionId, setMessages]);

  useEffect(() => {
    if (chatHistory && chatHistory.length > 0) {
      setMessages(chatHistory);
    }
  }, [chatHistory, setMessages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;
    sendMessage(input);
    setInput("");
  };

  const handleCardClick = useCallback((card: typeof ACTION_CARDS[number]) => {
    if (isStreaming) return;
    if (card.special === "meow") {
      const meow = MEOW_RESPONSES[Math.floor(Math.random() * MEOW_RESPONSES.length)]!;
      setMessages((prev) => [
        ...prev,
        { role: "user" as const, message: card.label },
        { role: "assistant" as const, message: meow },
      ]);
      return;
    }
    if (card.special === "open_question") {
      sendMessage("Ask me one short open-ended study question from the material. Just the question, no answer choices. I will answer and you evaluate my response.");
      return;
    }
    sendMessage(card.label);
  }, [isStreaming, sendMessage, setMessages]);

  const displayName = fileName || sessionTitle || "your material";

  if (isLoadingHistory) {
    return (
      <Card className="flex items-center justify-center h-[600px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </Card>
    );
  }

  const showEmptyState = messages.length === 0;

  return (
    <Card className="flex flex-col h-[600px] border border-border shadow-md overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6">
        <AnimatePresence mode="wait">
          {showEmptyState ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.35 }}
              className="min-h-full flex flex-col items-center justify-center px-4 py-6"
            >
              <div className="relative mb-3">
                <img
                  src={lerniCatFace}
                  alt="Lerni"
                  className="w-20 h-20 rounded-full object-cover drop-shadow-2xl"
                />
                <div className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-card shadow-sm" />
              </div>

              <h3 className="text-lg font-bold font-display mb-1">Lerni</h3>
              <p className="text-sm text-muted-foreground text-center max-w-md mb-5 leading-relaxed">
                I've read through your <span className="font-semibold text-foreground">{displayName}</span> — ask me anything!
              </p>

              <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
                {ACTION_CARDS.map((card) => {
                  const Icon = card.icon;
                  return (
                    <button
                      key={card.label}
                      onClick={() => handleCardClick(card)}
                      className={cn(
                        "group relative rounded-2xl border p-4 text-left transition-all duration-200",
                        "bg-gradient-to-br hover:scale-[1.02] active:scale-[0.98]",
                        "hover:shadow-lg cursor-pointer",
                        card.gradient,
                        card.border
                      )}
                    >
                      <Icon className="w-5 h-5 mb-2 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm font-medium leading-snug">{card.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="messages"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-6"
            >
              {messages.map((msg, i) => (
                <div key={i} className={cn("flex w-full gap-3", msg.role === "user" ? "justify-end" : "justify-start")}>
                  {msg.role === "assistant" && (
                    <img
                      src={lerniCatFace}
                      alt="Lerni"
                      className="w-9 h-9 rounded-full object-cover shrink-0 mt-1 ring-2 ring-border shadow-sm"
                    />
                  )}
                  <div className={cn(
                    "max-w-[80%] sm:max-w-[72%] rounded-2xl p-4",
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-sm shadow-md"
                      : "bg-muted text-foreground rounded-bl-sm"
                  )}>
                    {msg.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        <ReactMarkdown>{msg.message || (isStreaming && i === messages.length - 1 ? "Thinking..." : "")}</ReactMarkdown>
                      </div>
                    ) : (
                      <p>{msg.message}</p>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && (
        <div className="mx-4 mb-2 flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="p-4 bg-background border-t border-border rounded-b-2xl">
        <form onSubmit={onSubmit} className="relative flex items-center">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Lerni anything about your material..."
            className="min-h-[60px] h-[60px] pr-14 resize-none rounded-full"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
          />
          <Button
            type="submit"
            size="icon"
            className="absolute right-2 h-10 w-10 rounded-full"
            disabled={!input.trim() || isStreaming}
          >
            {isStreaming ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5 ml-1" />}
          </Button>
        </form>
      </div>
    </Card>
  );
}
